import { activeMonth } from "./activeMonth";
import type {
  Chip,
  DayCell,
  FounderMonth,
  LineKey,
  TodoItem,
  TodoTag,
  WeekData,
} from "./types";

/** Monday-first column headers. */
export const DOW_HEADER = ["一", "二", "三", "四", "五", "六", "日"] as const;

/** Index matches Date#getDay(): 0 = Sunday. */
export const DOW_FROM_JS = ["日", "一", "二", "三", "四", "五", "六"] as const;

export const CHIP_MAX = 3;

const WEEK_KAYOU: [string, string][] = [
  ["一", "锁题并窗"],
  ["二", "观心×2 ship"],
  ["三", "图文工作日件 ship"],
  ["四", "周末件前置审"],
  ["五", "观心×2＋统一定时＋周报"],
  ["六", "定时出／无交流"],
  ["日", "定时出／草案可拖周一"],
];

export type MonthRow = {
  wk: string;
  cells: DayCell[];
};

export type Slot = {
  label?: string;
  text: string;
  empty?: boolean;
  muted?: boolean;
};

export type DayColumns = {
  you: Slot[];
  ip: Slot[];
  co: Slot[];
};

export function dowOf(cell: { y: number; m: number; d: number }) {
  return new Date(cell.y, cell.m - 1, cell.d).getDay();
}

export function dowLabel(cell: { y: number; m: number; d: number }) {
  return DOW_FROM_JS[dowOf(cell)];
}

export function formatMd(m: number, d: number) {
  return `${m}/${d < 10 ? `0${d}` : d}`;
}

export function cellKey(cell: { y: number; m: number; d: number }) {
  return `${cell.y}-${cell.m}-${cell.d}`;
}

export function dayAriaLabel(cell: { m: number; d: number }) {
  return `${cell.m}月${cell.d}日`;
}

export function isSameCell(
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number },
) {
  return a.y === b.y && a.m === b.m && a.d === b.d;
}

/** Year and month of the grid. `id` is `YYYY-MM`; the wall clock does not move it. */
function viewYearMonth(month: FounderMonth) {
  const [y, m] = month.id.split("-").map(Number);
  return { y, m };
}

/** Calendar day in an IANA zone. Not `Date#toISOString`, which is UTC. */
export function calendarDay(timeZone: string, now: Date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    calendar: "gregory",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(now);
  const num = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return { y: num("year"), m: num("month"), d: num("day") };
}

export function todayCell(
  month: FounderMonth = activeMonth,
  now: Date = new Date(),
): DayCell {
  const { y, m, d } = calendarDay(month.timezone, now);
  const view = viewYearMonth(month);
  return {
    y,
    m,
    d,
    wk: month.weekKey(y, m, d) ?? "",
    out: y !== view.y || m !== view.m,
  };
}

export function extractTitles(value: string) {
  const out: string[] = [];
  const re = /《([^》]+)》/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value))) out.push(match[1]);
  return out;
}

export function shortFromTitle(title: string) {
  const text = String(title)
    .replace(/[「」"]/g, "")
    .trim();
  if (text.indexOf("纯过日子") === 0) return "纯过日子";
  if (text.length > 8) return text.slice(0, 8);
  return text;
}

export type CategoryKey =
  | "gx"
  | "tw"
  | "cg"
  | "xyz"
  | "cosy"
  | "geo"
  | "nm"
  | "you"
  | "co"
  | "idea"
  | "soft";

export type CategoryKind = "biz" | "idea" | "matter";

export type Category = {
  label: string;
  key: CategoryKey;
  kind: CategoryKind;
};

function category(
  label: string,
  key: CategoryKey,
  kind: CategoryKind,
): Category {
  return { label, key, kind };
}

/**
 * Classification v3 display names.
 * Legacy shorts (观心 / 图文 / 草稿 / 公司 / 卡你 / 小宇宙 / 打卡连连看) are read aliases.
 * Business lines are matched before product ideas, and ideas before 公司事项,
 * so an unopened idea is never labeled 公司事项.
 * Product ideas are not opened lines. 打工牛马连连看 is biz `biz.niuma` / `nm`.
 */
export function categoryOf(name: string): Category | null {
  const text = name.trim();
  if (!text) return null;
  if (text.includes("GEO")) return category("GEO", "geo", "biz");
  if (text.includes("观心")) return category("观心微语", "gx", "biz");
  if (text.includes("图文")) return category("小凡的图文故事", "tw", "biz");
  if (text.includes("草稿")) return category("小凡的草稿本", "cg", "biz");
  if (text.includes("读书笔记") || text.includes("小宇宙"))
    return category("小凡的读书笔记", "xyz", "biz");
  if (/CosyTab/i.test(text)) return category("CosyTab", "cosy", "biz");
  if (text.includes("打工牛马") || text.includes("打卡连连看"))
    return category("打工牛马连连看", "nm", "biz");
  if (text.includes("童心会话"))
    return category("童心会话视频", "idea", "idea");
  if (text.includes("公司")) return category("公司事项", "co", "matter");
  if (text === "交流" || text === "卡你")
    return category("交流", "you", "matter");
  if (text === "默跑") return category("默跑", "soft", "matter");
  return null;
}

export function tagLabel(tag: TodoTag) {
  if (tag === "you" || tag === "talk") return "交流";
  if (tag === "co") return "公司事项";
  if (tag === "soft") return "默跑";
  return "";
}

export function partitionChips<T>(items: T[], max = CHIP_MAX) {
  return { visible: items.slice(0, max), rest: items.slice(max) };
}

export function buildMonthRows(month: FounderMonth = activeMonth): MonthRow[] {
  const { y: year, m: monthNumber } = viewYearMonth(month);
  const first = new Date(year, monthNumber - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const cursor = new Date(year, monthNumber - 1, 1 - lead);
  const lastDate = new Date(year, monthNumber, 0).getDate();
  const lastDow = (new Date(year, monthNumber - 1, lastDate).getDay() + 6) % 7;
  const end = new Date(year, monthNumber - 1, lastDate + (6 - lastDow));

  const cells: DayCell[] = [];
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth() + 1;
    const d = cursor.getDate();
    cells.push({
      y,
      m,
      d,
      wk: month.weekKey(y, m, d) ?? "",
      out: m !== monthNumber || y !== year,
    });
    cursor.setDate(d + 1);
  }

  const rows: MonthRow[] = [];
  for (let index = 0; index < cells.length; index += 7) {
    const slice = cells.slice(index, index + 7);
    const inMonth = slice.find((cell) => !cell.out);
    rows.push({ wk: inMonth?.wk || slice[0]?.wk || "", cells: slice });
  }
  return rows;
}

export function daysInWeek(rows: MonthRow[], wk: string) {
  const inside = rows
    .flatMap((row) => row.cells)
    .filter((cell) => cell.wk === wk && !cell.out);
  if (inside.length) return inside;
  return rows.flatMap((row) => row.cells).filter((cell) => cell.wk === wk);
}

export function statusLabelFor(
  cell: DayCell,
  month: FounderMonth = activeMonth,
) {
  const week = month.weeks[cell.wk];
  if (!week) return "";
  if (week.status === "now") return "本周";
  if (week.status === "past") return "过去";
  if (week.marks.empty) return "待锁／未来";
  return week.statusText || "未来";
}

export function buildTodoItems(
  cell: DayCell,
  month: FounderMonth = activeMonth,
): TodoItem[] {
  const dow = dowOf(cell);
  const week = month.weeks[cell.wk];
  const base = month.kayouByDow[dow];
  let items: TodoItem[] = [];

  if (cell.wk === month.dayExtrasWeek && month.dayExtras[cell.d]) {
    items = items.concat(month.dayExtras[cell.d]);
  } else if (week && week.status === "past") {
    items.push({ tag: "soft", text: "过去日 · 无待办交流（题名见周栏实填）" });
  } else if (week && week.label === "W49") {
    items.push({ tag: "soft", text: "缓冲 · 为 12 月备货；非 L3 不扰" });
    items.push({ tag: "co", text: month.coByDow[dow] || "默跑" });
  } else if (week && week.marks.empty) {
    if (base) items.push(...base.items);
    items.push({
      tag: "ip",
      text: `题名待锁（周一回填）· ${month.ipByDow[dow] || ""}`,
    });
    items.push({ tag: "co", text: month.coByDow[dow] || "默跑" });
  } else {
    if (base) items.push(...base.items);
    items.push({ tag: "ip", text: month.ipByDow[dow] || "按专历" });
    items.push({ tag: "co", text: month.coByDow[dow] || "默跑" });
  }

  if (!items.length) items.push({ tag: "soft", text: "默跑／无交流" });
  return items;
}

export function kayouHintFor(cell: DayCell, month: FounderMonth = activeMonth) {
  const items = buildTodoItems(cell, month);
  const you = items
    .filter((item) => item.tag === "you")
    .map((item) => item.text);
  if (you.length) return you.slice(0, 2).join("；");
  return (
    items
      .slice(0, 2)
      .map((item) => item.text)
      .join("；") || "默跑／无交流"
  );
}

function makeChip(
  partial: {
    id: string;
    short: string;
    title: string;
    line: string;
    lineKey: LineKey;
    status?: string;
    kayou?: string;
    body?: string;
  },
  fallbackStatus: string,
  fallbackKayou: string,
): Chip {
  return {
    id: partial.id,
    short: partial.short,
    title: partial.title,
    line: partial.line,
    lineKey: partial.lineKey,
    status: partial.status || fallbackStatus,
    kayou: partial.kayou || fallbackKayou,
    ...(partial.body ? { body: partial.body } : {}),
  };
}

const TALK_ROUTINE =
  "与创始人例行过目——工作日日计划／日报；周一另有周计划；周四前后周末件前置过目；周五另有周报；周末默认少扰。";

const TALK_WINDOWS: {
  id: string;
  name: string;
  dows: number[];
  lead: string;
}[] = [
  {
    id: "day",
    name: "日计划／日报",
    dows: [1, 2, 3, 4, 5],
    lead: "要交流的是当天的日计划和日报：早上约 08:30 过日计划，傍晚约 17:30 过日报。",
  },
  {
    id: "week-plan",
    name: "周计划",
    dows: [1],
    lead: "要交流的是本周计划：周一约 08:40 过周计划，并把本周题锁上。",
  },
  {
    id: "weekend-pre",
    name: "周末件前置审",
    dows: [4],
    lead: "要交流的是周末件前置审：周四前后先过周六图文和周日草稿。没齐的，最迟周五上午补审。",
  },
  {
    id: "week-report",
    name: "周报",
    dows: [5],
    lead: "要交流的是本周周报：周五约 17:45 过周报，和当天日报接在一起。",
  },
];

/** W45 point for the window it belongs to. Weekends stay off the grid. */
const W45_TALK_POINT: Record<number, { id: string; text: string }> = {
  2: { id: "week-plan", text: "锁题＋周计划" },
  3: { id: "day", text: "观心微语×2审美＋ship" },
  4: {
    id: "day",
    text: "小凡的图文故事《借着天凉》已发（小红书＋抖音图文小号）",
  },
  5: {
    id: "weekend-pre",
    text: "《字条和277块》《「纯过日子」到底赢在哪》明天交流",
  },
  6: { id: "week-report", text: "观心×2＋统一定时＋周报" },
};

/** W45 Thursday only: these two pieces are 明天交流, not the standing 前置审 slot. */
function w45WeekendPieces(cell: DayCell, windowId: string) {
  return cell.wk === "W45" && windowId === "weekend-pre";
}

function talkBody(cell: DayCell, window: (typeof TALK_WINDOWS)[number]) {
  const lines = [
    ...(w45WeekendPieces(cell, window.id) ? [] : [window.lead]),
    TALK_ROUTINE,
    "总周历 ops/FOUNDER_WEEKLY_CALENDAR.md",
  ];
  const point = cell.wk === "W45" ? W45_TALK_POINT[cell.d] : undefined;
  if (point?.id === window.id) lines.push(`当日：${point.text}`);
  return lines.join("\n");
}

function pushTalkWindows(
  push: (partial: Parameters<typeof makeChip>[0]) => void,
  cell: DayCell,
  idPrefix: string,
  status?: string,
) {
  const dow = dowOf(cell);
  for (const window of TALK_WINDOWS) {
    if (!window.dows.includes(dow)) continue;
    // Daily plan differs every day. A shared title would join Mon–Fri into one span.
    // W45's two weekend pieces say 明天交流; later Thursdays keep the slot name.
    const pieces = w45WeekendPieces(cell, window.id);
    const title = pieces
      ? "明天交流"
      : window.id === "day"
        ? `${window.name} · ${formatMd(cell.m, cell.d)}`
        : window.name;
    push({
      id: `${idPrefix}-d${cell.d}-${window.id}`,
      short: pieces ? "明天交流" : window.name,
      title,
      line: "交流",
      lineKey: "you",
      body: talkBody(cell, window),
      ...(status ? { status } : {}),
    });
  }
}

/** Standing weekday window. Same A-tier as empty-week 交流／公司事项. */
function isWeekday(dow: number) {
  return dow >= 1 && dow <= 5;
}

const NIUMA_LINE = "打工牛马连连看";
const NIUMA_TITLE = "调研进行中·六条思路备忘";
const NIUMA_BODY =
  "业务线 biz.niuma。真源 ops/products/打工牛马连连看/思路备忘-2026-11-04.md · deal-3 · Tasks#17";

function pushNiuma(
  push: (partial: Parameters<typeof makeChip>[0]) => void,
  cell: DayCell,
) {
  push({
    id: `${cell.wk}-nm-${cell.d}`,
    short: NIUMA_LINE,
    title: NIUMA_TITLE,
    line: NIUMA_LINE,
    lineKey: "nm",
    status: "调研进行中",
    body: NIUMA_BODY,
  });
}

/**
 * CosyTab／GEO 固定周槽. Titles stay short and already in the month payload —
 * no invented 选题. W47 GEO窗 is the Wed–Fri 验收窗, not this Mon–Fri 默跑.
 * 小宇宙「本周不带／有料才带」is not a standing slot.
 * Unopened ideas stay legend-only. 打工牛马连连看 is an opened biz line.
 */
function pushStandingBiz(
  push: (partial: Parameters<typeof makeChip>[0]) => void,
  cell: DayCell,
  week: WeekData,
) {
  if (!isWeekday(dowOf(cell))) return;
  push({
    id: `${cell.wk}-cosy-${cell.d}`,
    short: "CosyTab",
    title: week.label === "W46" ? "CosyTab 周复盘短报" : "CosyTab 默跑",
    line: "CosyTab",
    lineKey: "cosy",
  });
  if (week.label === "W47") return;
  push({
    id: `${cell.wk}-geo-${cell.d}`,
    short: "GEO窗",
    title: "GEO窗",
    line: "GEO",
    lineKey: "geo",
  });
}

/**
 * Empty-week slot cadence (JS getDay). 观心·二 is Mon–Tue, not Mon–Wed.
 * 草稿·日 is Thu–Sun. Unopened product ideas stay legend-only.
 */
function emptySlotDows(label: string): number[] | null {
  if (label.endsWith("·二")) return [1, 2];
  if (label.endsWith("·五")) return [3, 4, 5];
  if (label.endsWith("·三")) return [2, 3];
  if (label.endsWith("·六")) return [4, 5, 6];
  if (label.endsWith("·日")) return [4, 5, 6, 0];
  return null;
}

/**
 * Day chips for the 2026-11 prototype.
 * Titles are taken from week slots and weekday templates — not invented here.
 * Same `lineKey`+`title` on adjacent days becomes one `lanesForWeek` span.
 */
export function chipsForDay(
  cell: DayCell,
  month: FounderMonth = activeMonth,
): Chip[] {
  if (cell.out) return [];
  // ponytail: DB months carry explicit bars; static months keep the rules below.
  if (month.dayBars) return month.dayBars[cellKey(cell)] ?? [];
  const week = month.weeks[cell.wk];
  if (!week) return [];
  const dow = dowOf(cell);
  const list: Chip[] = [];
  const status = statusLabelFor(cell, month);
  const hint = kayouHintFor(cell, month);
  const push = (partial: Parameters<typeof makeChip>[0]) => {
    list.push(makeChip(partial, status, hint));
  };
  const pushTitles = (
    raw: string,
    idPrefix: string,
    line: string,
    lineKey: LineKey,
    exclude?: Set<string>,
  ) => {
    extractTitles(raw)
      .filter((title) => !exclude?.has(title))
      .forEach((title, index) => {
        push({
          id: `${idPrefix}${index}`,
          short: shortFromTitle(title),
          title: `《${title}》`,
          line,
          lineKey,
        });
      });
  };

  if (cell.wk === "W45" && cell.m === 11) {
    const d = cell.d;
    if (d === 2) {
      push({
        id: "w45-d2-ky",
        short: "周题已锁",
        title: "周题已锁",
        line: "交流",
        lineKey: "you",
        kayou: hint,
        status: "已过",
      });
    }
    // 免打扰／稍等: restyle/ship still open on 2026-11-03, so 2–4 not past 3.
    if (d >= 2 && d <= 4) {
      pushTitles(week.ip[0][1], `w45-d${d}-gx2-`, "观心微语", "gx");
    }
    if (d >= 3 && d <= 4) {
      pushTitles(week.ip[2][1], `w45-d${d}-tw3-`, "小凡的图文故事", "tw");
    }
    if (
      d === 3 &&
      month.dayExtras[d]?.some((item) => item.tag === "ip" && item.done)
    ) {
      for (const chip of list) {
        if (chip.id.startsWith("w45-d3-tw3-")) chip.status = "已过";
      }
    }
    if (d >= 4 && d <= 6) {
      pushTitles(week.ip[1][1], `w45-d${d}-gx5-`, "观心微语", "gx");
    }
    if (d >= 5 && d <= 7) {
      pushTitles(week.ip[3][1], `w45-d${d}-tw6-`, "小凡的图文故事", "tw");
    }
    if (d >= 5 && d <= 8) {
      pushTitles(week.ip[4][1], `w45-d${d}-cg-`, "小凡的草稿本", "cg");
    }
    pushTalkWindows(push, cell, "w45");
    if (d === 5) {
      push({
        id: "w45-d5-co",
        short: "用量重置",
        title: "用量重置约 11:35 Asia/Shanghai",
        line: "公司事项",
        lineKey: "co",
      });
    }
    pushStandingBiz(push, cell, week);
    if (d === 4) pushNiuma(push, cell);
    return list;
  }

  if (cell.wk === "W44" && cell.m === 11 && cell.d === 1) {
    const draft = week.ip[2][1];
    const titles = extractTitles(draft);
    push({
      id: "w44-d1-cg",
      short: titles[0] ? shortFromTitle(titles[0]) : "草稿已发",
      title: draft,
      line: "小凡的草稿本",
      lineKey: "cg",
      status: "已发／过去",
    });
    return list;
  }

  if (cell.wk === "W49" && cell.m === 11 && cell.d === 30) {
    push({
      id: "w49-co",
      short: "公司事项",
      title: "为 12 月周计划备货",
      line: "公司事项",
      lineKey: "co",
      status: "缓冲",
    });
    return list;
  }

  if (week.marks.empty) {
    if (week.marks.you) {
      pushTalkWindows(push, cell, cell.wk, "待锁／未来");
    }
    if (week.marks.co && dow >= 1 && dow <= 5) {
      push({
        id: `${cell.wk}-co-${cell.d}`,
        short: "公司事项",
        title: "公司事项",
        line: "公司事项",
        lineKey: "co",
        status: "未来",
      });
    }
    for (const [label, value] of week.ip) {
      if (value !== "待锁") continue;
      const days = emptySlotDows(label);
      const found = categoryOf(label);
      if (!days?.includes(dow) || !found || found.kind !== "biz") continue;
      push({
        id: `${cell.wk}-slot-${label}-${cell.d}`,
        short: label,
        title: label,
        line: found.label,
        lineKey: found.key as LineKey,
        status: "待锁／未来",
      });
    }
    if (cell.wk === "W47" && (dow === 3 || dow === 4 || dow === 5)) {
      push({
        id: `${cell.wk}-geo-${cell.d}`,
        short: "GEO窗",
        title: "GEO 验收窗 · Phase A 四周判定",
        line: "GEO",
        lineKey: "geo",
        status: "未来",
      });
    }
    pushStandingBiz(push, cell, week);
    if (cell.wk === "W46" && isWeekday(dow)) pushNiuma(push, cell);
  }
  return list;
}

function ipPicks(cell: DayCell, week: WeekData) {
  const dow = dowOf(cell);
  if (cell.wk === "W45") {
    const picks: [string, string][] = [];
    if (dow === 1 || dow === 2) picks.push(week.ip[0]);
    if (dow === 5) picks.push(week.ip[1]);
    if (dow === 3) picks.push(week.ip[2]);
    if (dow === 6) picks.push(week.ip[3]);
    if (dow === 0) picks.push(week.ip[4]);
    if (dow === 4) {
      picks.push(week.ip[3]);
      picks.push(week.ip[4]);
    }
    return picks;
  }
  if (cell.wk === "W44" && cell.d === 1) return [week.ip[2]];
  if (week.marks.empty) {
    if (dow === 2) return [week.ip[0]];
    if (dow === 5) return [week.ip[1]];
    if (dow === 3) return [week.ip[2]];
    if (dow === 6) return [week.ip[3]];
    if (dow === 0) return [week.ip[4]];
    if (dow === 1) return week.ip.slice(0, 5);
    if (dow === 4) return [week.ip[3], week.ip[4]];
  }
  if (cell.wk === "W49") return week.ip;
  return [];
}

function slotFromPair(pair: [string, string] | undefined): Slot | null {
  if (!pair) return null;
  const [label, value] = pair;
  const empty = value === "待锁" || value.startsWith("非本月");
  return { label, text: value, empty };
}

export function dayFilteredCols(
  cell: DayCell,
  month: FounderMonth = activeMonth,
): DayColumns {
  const dow = dowOf(cell);
  const week = month.weeks[cell.wk];
  const kayou = month.kayouByDow[dow];
  const you: Slot[] = [];

  if (cell.wk === "W45" && month.dayExtras[cell.d]) {
    const focused = month.dayExtras[cell.d].filter(
      (item) => item.tag === "you" || item.tag === "talk",
    );
    focused.forEach((item) => {
      you.push({ label: tagLabel(item.tag), text: item.text });
    });
  }
  if (!you.length) {
    if (kayou) {
      you.push({ label: "窗", text: `${kayou.win} · ${kayou.title}` });
      kayou.items.forEach((item) => {
        you.push({ label: tagLabel(item.tag), text: item.text });
      });
    } else {
      you.push({ text: "无交流窗", empty: true });
    }
  }

  const ip: Slot[] = [];
  if (week) {
    ipPicks(cell, week).forEach((pair) => {
      const slot = slotFromPair(pair);
      if (slot) ip.push(slot);
    });
    ip.push({ label: "日节奏", text: month.ipByDow[dow] || "—" });
  }
  if (!ip.length) ip.push({ text: "本日无题槽", empty: true });

  const co: Slot[] = [{ text: month.coByDow[dow] || "默跑" }];
  if (cell.wk === "W45" && month.dayExtras[cell.d]) {
    month.dayExtras[cell.d]
      .filter((item) => item.tag === "co")
      .forEach((item) => {
        co.push({ label: "当日", text: item.text });
      });
  }
  if (week && dow === 1) {
    co.push({ text: `周事项摘：${week.co}`, muted: true });
  }

  return { you, ip, co };
}

export function weekKayouSlots(week: WeekData | undefined): Slot[] {
  if (week?.status === "past" && week.label === "W44") {
    return [{ text: "过去周 · 交流窗已过；见题名实填", empty: true }];
  }
  if (week?.label === "W49") {
    return [{ text: "缓冲日 · 无主产交流", empty: true }];
  }
  return WEEK_KAYOU.map(([label, text]) => ({ label, text }));
}

export type LaneChip = {
  chip: Chip;
  /** Inclusive column indexes in the week row. A span is one chip across these columns. */
  start: number;
  end: number;
};

/**
 * Same line + title on consecutive days becomes one span.
 * 日计划／日报 is keyed by id as well: each weekday has its own title, and
 * must not join even if two days ever share a title.
 */
const spanKey = (chip: Chip) => {
  if (chip.spanId) return chip.spanId;
  if (chip.short === "日计划／日报") return chip.id;
  return `${chip.lineKey}\u0000${chip.title}`;
};

/**
 * One lane row per non-overlapping chip. The same title on consecutive days in
 * the week becomes a single span. A single-day chip keeps its lane, so the next
 * day stays empty instead of letting a neighbor float up. Later days reuse a
 * lane once that chip has ended.
 */
export function lanesForWeek(
  cells: DayCell[],
  month: FounderMonth = activeMonth,
): LaneChip[][] {
  const grouped = new Map<string, { chip: Chip; cols: number[] }>();
  const order: string[] = [];

  cells.forEach((cell, col) => {
    if (cell.out) return;
    for (const chip of chipsForDay(cell, month)) {
      const key = spanKey(chip);
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, { chip, cols: [col] });
        order.push(key);
        continue;
      }
      const last = existing.cols[existing.cols.length - 1];
      if (last === col - 1) {
        existing.cols.push(col);
        continue;
      }
      if (last === col) continue;
      const splitKey = `${key}\u0000${col}`;
      grouped.set(splitKey, { chip, cols: [col] });
      order.push(splitKey);
    }
  });

  const lanes: LaneChip[][] = [];
  for (const key of order) {
    const entry = grouped.get(key);
    if (!entry) continue;
    const item: LaneChip = {
      chip: entry.chip,
      start: entry.cols[0],
      end: entry.cols[entry.cols.length - 1],
    };
    const lane = lanes.find((row) =>
      row.every((other) => item.end < other.start || item.start > other.end),
    );
    if (lane) lane.push(item);
    else lanes.push([item]);
  }
  return lanes;
}

/**
 * Bars painted in one day cell, top to bottom.
 * Same lane packing as `lanesForWeek`, including spans that start on an earlier day.
 */
export function chipsInLaneOrder(
  cell: DayCell,
  weekCells: DayCell[],
  month: FounderMonth = activeMonth,
): Chip[] {
  const col = weekCells.findIndex((day) => isSameCell(day, cell));
  const dayChips = chipsForDay(cell, month);
  if (col < 0) return dayChips;
  const used = new Set<number>();
  const ordered: Chip[] = [];
  for (const lane of lanesForWeek(weekCells, month)) {
    for (const item of lane) {
      if (col < item.start || col > item.end) continue;
      const key = spanKey(item.chip);
      const index = dayChips.findIndex(
        (chip, chipIndex) => !used.has(chipIndex) && spanKey(chip) === key,
      );
      if (index < 0) continue;
      used.add(index);
      ordered.push(dayChips[index]);
    }
  }
  return ordered;
}

export function weekIpSlots(week: WeekData | undefined): {
  slots: Slot[];
  note?: string;
} {
  if (!week) return { slots: [] };
  return {
    slots: week.ip.map(([label, text]) => ({
      label,
      text,
      empty: text === "待锁" || text.startsWith("非本月"),
    })),
    note: week.marks.empty ? "周一锁题后回填" : undefined,
  };
}
